import type { AnyObj } from '@/types';
import bwPowerSet from './bwPowerSet';

// 用于动态获取嵌套属性的函数，传入对象和路径，返回对应值
function getNestedValue(obj: Record<string, any>, path: string): any {
  return path
    .split('.')
    .reduce((acc, key) => (acc !== null && acc !== undefined ? acc[key] : undefined), obj);
}

export default {
  /**
   * 根据路径字符串查找目标数据
   * eg: 'orderPromotionResult-id|1398-promResult-orderLinePromResults-skuId|3324'
   * @param data
   * @param path
   * @returns
   */
  find: (data: AnyObj, path: string) => {
    if (!data || !path) {
      console.log(`<相关查找参数不能为空>`, { data, path });
      return;
    }

    const pathArr = path.split('-');
    const pathArrLen = pathArr.length;

    let result = data;

    for (let i = 0; i < pathArrLen; i++) {
      const key = pathArr[i];
      if (key.includes('|')) {
        const keyArr = key.split('|');
        result = result?.find((it: AnyObj) => it[keyArr[0]] === keyArr[1]);
      } else {
        result = result[key];
      }
    }

    return result;
  },

  /**
   * 计算最小值和最大值
   * @param arr
   * @param path
   * @returns
   */
  getMinMax(arr: Record<string, any>[], path: string) {
    let min = 0;
    let max = 0;
    // 标记是否找到有效数值
    let foundValid = false;

    for (let i = 0; i < arr.length; i++) {
      const value = getNestedValue(arr[i], path); // 获取嵌套的值，处理 null

      // 检查值是否为有效的数值（排除 null, undefined, NaN, 非数字类型）
      if (typeof value === 'number' && !isNaN(value)) {
        if (!foundValid) {
          // 第一次找到有效数值时，将 min 和 max 初始化为该数值
          min = max = value;
          foundValid = true;
        } else {
          // 更新 min 和 max
          if (value < min) min = value;
          if (value > max) max = value;
        }
      }
    }

    // 如果没有找到有效的数值，min 和 max 保持为 0
    return { min, max };
  },
  /**
   * 获取模拟数据
   * @param num
   * @param desc
   * @returns
   */
  getMock(num = 10, desc = 'mock') {
    return new Array(num).fill(0).map((_, index) => {
      return `${desc}： ${index}`;
    });
  },
  /**
   * 获取随机字符串
   * @param length
   * @returns
   */
  getRandomId(length: number) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result.toLowerCase();
  },
  /**
   * 将数字转换为带"w"单位的字符串
   * @param num - 需要转换的数字
   * @param decimal - 保留的小数位数，默认为1
   * @returns 格式化后的字符串
   *
   * @example
   * formatNumberToW(15000) // 返回 "1.5w"
   * formatNumberToW(9999) // 返回 "9999"
   * formatNumberToW(150000, 2) // 返回 "15.00w"
   */
  formatNumberToW(num: number, decimal: number = 1) {
    // 如果数字小于10000，直接返回原数字
    if (num < 10000) {
      return String(num);
    }

    // 将数字除以10000并保留指定位数的小数
    const formatted = (num / 10000).toFixed(decimal);

    // 去除末尾多余的0和小数点
    const trimmed = formatted.replace(/\.?0+$/, '');

    // 添加w单位
    return `${trimmed}w`;
  },
  /**
   * API请求重试函数
   * @param {Function} fetchFn - 需要执行的查询函数，接收参数并返回Promise
   * @param {any} fetchParams - 传递给查询函数的参数
   * @param {Function} validateFn - 验证结果是否有效的函数，接收结果作为参数并返回布尔值，默认检查结果是否存在
   * @param {number} maxRetries - 最大重试次数，默认3次
   * @param {number} retryDelay - 重试间隔时间(毫秒)，默认2000ms
   * @returns {Promise<any>} 查询结果
   */
  fetchWithRetry: async (
    fetchFn: Function,
    fetchParams: any,
    validateFn: Function,
    maxRetries = 3,
    retryDelay = 1000
  ): Promise<any> => {
    let retryCount = 0;
    let result = null;

    // 定义等待函数
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    // 执行查询（最多maxRetries次）
    while (retryCount < maxRetries) {
      result = await fetchFn(fetchParams);
      console.log(`执行查询`, { result, retryCount });

      // 使用验证函数检查结果是否有效
      if (validateFn(result)) {
        break;
      }

      // 增加重试计数
      retryCount++;

      // 如果已经达到最大重试次数，跳出循环
      if (retryCount >= maxRetries) {
        break;
      }

      // 等待指定时间后重试
      await wait(retryDelay);
    }

    return result;
  },
  bwPowerSet,
};
